import { useState, useEffect, useCallback } from "react";
import ReactApexChart from "react-apexcharts";

const EarningStatisticTwo = () => {
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        height: 350,
        type: 'area',
        toolbar: { show: false }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' },
      xaxis: {
        categories: [],
        labels: { style: { colors: '#6B7280', fontSize: '12px' } }
      },
      yaxis: {
        labels: { style: { colors: '#6B7280', fontSize: '12px' } }
      },
      tooltip: { theme: 'light' },
      colors: ['#3B82F6', '#F59E0B'],
      legend: { show: false },
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4,
        yaxis: { lines: { show: true } }
      }
    }
  });

  const [totalDepartmental, setTotalDepartmental] = useState(0);
  const [totalPublic, setTotalPublic] = useState(0);
  const [provinceTotals, setProvinceTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('national');
  const [rawData, setRawData] = useState([]);
  const [provinceColors] = useState([
    '#3B82F6', '#F59E0B', '#10B981', '#EF4444',
    '#8B5CF6', '#EC4899', '#06B6D4'
  ]);

  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Memoize fetch function
  const fetchPlantationData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/plantation/pafs/month-wise-plantation`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();

      if (result.success && result.data) {
        setRawData(result.data);
        processChartData(result.data);
      }
    } catch (error) {
      console.error('Error fetching plantation data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]); // Only recreate if API URL changes

  // Initial load
  useEffect(() => {
    fetchPlantationData();
  }, [fetchPlantationData]); // Now includes fetchPlantationData

  const processChartData = (data) => {
    const months = data.map(item => item.month);
    const departmentalData = data.map(item => item.departmental);
    const publicData = data.map(item => item.public);

    const departmentalTotal = departmentalData.reduce((sum, v) => sum + v, 0);
    const publicTotal = publicData.reduce((sum, v) => sum + v, 0);
    setTotalDepartmental(departmentalTotal);
    setTotalPublic(publicTotal);

    const allProvinces = [
      "Punjab", "Sindh", "Khyber Pakhtunkhwa", "Balochistan",
      "Islamabad Capital Territory", "Azad Jammu & Kashmir", "Gilgit-Baltistan"
    ];

    const provinceTotalsCalc = {};
    allProvinces.forEach(p => provinceTotalsCalc[p] = 0);

    data.forEach(monthData => {
      monthData.provinces.forEach(p => {
        if (provinceTotalsCalc.hasOwnProperty(p.province)) {
          provinceTotalsCalc[p.province] += p.total;
        }
      });
    });
    setProvinceTotals(provinceTotalsCalc);

    // National view
    setChartData(prev => ({
      ...prev,
      series: [
        { name: 'Departmental', data: departmentalData },
        { name: 'Public', data: publicData }
      ],
      options: {
        ...prev.options,
        xaxis: { ...prev.options.xaxis, categories: months },
        colors: ['#3B82F6', '#F59E0B']
      }
    }));
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);

    if (mode === 'national') {
      const months = rawData.map(item => item.month);
      const departmentalData = rawData.map(item => item.departmental);
      const publicData = rawData.map(item => item.public);

      setChartData(prev => ({
        ...prev,
        series: [
          { name: 'Departmental', data: departmentalData },
          { name: 'Public', data: publicData }
        ],
        options: {
          ...prev.options,
          xaxis: { ...prev.options.xaxis, categories: months },
          colors: ['#3B82F6', '#F59E0B']
        }
      }));
    } else if (mode === 'provincial') {
      const months = rawData.map(item => item.month);
      const allProvinces = [
        "Punjab", "Sindh", "Khyber Pakhtunkhwa", "Balochistan",
        "Islamabad Capital Territory", "Azad Jammu & Kashmir", "Gilgit-Baltistan"
      ];

      const provincialSeries = allProvinces.map((province, index) => {
        const provinceData = months.map(month => {
          const monthData = rawData.find(item => item.month === month);
          if (monthData) {
            const entry = monthData.provinces.find(p => p.province === province);
            return entry ? entry.total : 0;
          }
          return 0;
        });
        return { name: province, data: provinceData };
      });

      setChartData(prev => ({
        ...prev,
        series: provincialSeries,
        options: {
          ...prev.options,
          xaxis: { ...prev.options.xaxis, categories: months },
          colors: provinceColors
        }
      }));
    }
  };

  if (loading) {
    return (
      <div className='col-xxl-8'>
        <div className='card h-100'>
          <div className='card-body p-24 d-flex justify-content-center align-items-center'>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='col-xxl-8' style={{ marginTop: '16px' }}>
      <div className='card h-100'>
        <div className='card-header'>
          <div className='d-flex align-items-center flex-wrap gap-2 justify-content-between'>
            <h6 className='mb-2 fw-bold text-lg mb-0'>Plantation Statistics</h6>
            <select
              className='form-select form-select-sm w-auto bg-base border-0 text-secondary-light'
              value={viewMode}
              onChange={(e) => handleViewModeChange(e.target.value)}
            >
              <option value="national">National Statistics</option>
              <option value="provincial">Provincial Statistics</option>
            </select>
          </div>
        </div>

        <div className='card-body p-24'>
          {viewMode === 'national' ? (
            <ul className='d-flex flex-wrap align-items-center justify-content-center my-3 gap-3'>
              <li className='d-flex align-items-center gap-2'>
                <span className='w-12-px h-8-px rounded-pill bg-primary-600' />
                <span className='text-secondary-light text-sm fw-semibold'>
                  Departmental:
                  <span className='text-primary-light fw-bold'> {totalDepartmental}</span>
                </span>
              </li>
              <li className='d-flex align-items-center gap-2'>
                <span className='w-12-px h-8-px rounded-pill bg-warning-600' />
                <span className='text-secondary-light text-sm fw-semibold'>
                  Public:
                  <span className='text-primary-light fw-bold'> {totalPublic}</span>
                </span>
              </li>
            </ul>
          ) : (
            <ul className='d-flex flex-wrap align-items-center justify-content-center my-3 gap-3'>
              {Object.entries(provinceTotals).map(([province, total], index) => {
                const shortName = province === 'Islamabad Capital Territory'
                  ? 'ICT'
                  : province === 'Azad Jammu & Kashmir'
                    ? 'AJK'
                    : province.split(' ')[0];

                return (
                  <li key={province} className='d-flex align-items-center gap-2'>
                    <span
                      className='w-12-px h-8-px rounded-pill'
                      style={{ backgroundColor: provinceColors[index] }}
                    />
                    <span className='text-secondary-light text-sm fw-semibold'>
                      {shortName}:
                      <span className='text-primary-light fw-bold'> {total}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <div id='plantationChart' className='apexcharts-tooltip-style-1'>
            <ReactApexChart
              options={chartData.options}
              series={chartData.series}
              type='area'
              height={260}
              width="100%"
            />
          </div>
        </div>
      </div>
    </div>
  );
};


export default EarningStatisticTwo;